/**
 * Global submission focus (A2F-454, A2F-459).
 *
 * Provides a single app-level "which submission am I working on?" selector
 * that all modules read from. Also fetches explorer docs and builds the
 * docSubmissionMap so filtering works on any page, not just after visiting
 * the Dashboard.
 *
 * The sidebar renders the picker. Modules read submissionFocusId to filter.
 * Planning View ignores this (uses its own is_planning filter).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from './api';
import { useProject } from './ProjectContext';
import { buildDocSubmissionMap, type DocSubmissionMap } from './findingClassification';
import type { Submission } from '@/types/api';

interface SubmissionFocusContextValue {
  /** Currently focused submission ID (null = "All Submissions"). */
  submissionFocusId: string | null;
  /** Set the focused submission. */
  setSubmissionFocusId: (id: string | null) => void;
  /** Full Submission object for the focused submission (for display). */
  focusedSubmission: Submission | null;
  /** All submissions for the active project. */
  submissions: Submission[];
  /** True while submissions are loading. */
  loadingSubmissions: boolean;
  /** Precomputed docId → submissionId map for filtering (A2F-459). */
  docSubmissionMap: DocSubmissionMap | null;
}

const SubmissionFocusContext = createContext<SubmissionFocusContextValue>({
  submissionFocusId: null,
  setSubmissionFocusId: () => {},
  focusedSubmission: null,
  submissions: [],
  loadingSubmissions: false,
  docSubmissionMap: null,
});

export function useSubmissionFocus() {
  return useContext(SubmissionFocusContext);
}

export function SubmissionFocusProvider({ children }: { children: ReactNode }) {
  const { activeProject } = useProject();
  const [focusId, setFocusId] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(false);
  const [docSubMap, setDocSubMap] = useState<DocSubmissionMap | null>(null);
  const projectIdRef = useRef<string | undefined>(undefined);

  // Fetch submissions + explorer docs when project changes
  useEffect(() => {
    const pid = activeProject?.id;
    if (pid !== projectIdRef.current) {
      projectIdRef.current = pid;
      setFocusId(null);
      setSubmissions([]);
      setDocSubMap(null);
      if (!pid) return;

      setLoading(true);
      Promise.allSettled([
        api.projects.submissions(pid, true),
        api.documents.explorer(pid),
      ]).then(([subsR, explorerR]) => {
        setSubmissions(subsR.status === 'fulfilled' ? subsR.value : []);
        if (explorerR.status === 'fulfilled') {
          setDocSubMap(buildDocSubmissionMap(explorerR.value.documents));
        }
      }).finally(() => setLoading(false));
    }
  }, [activeProject?.id]);

  const setSubmissionFocusId = useCallback((id: string | null) => {
    setFocusId(id);
  }, []);

  const focusedSubmission = focusId
    ? submissions.find(s => s.id === focusId) ?? null
    : null;

  return (
    <SubmissionFocusContext.Provider
      value={{
        submissionFocusId: focusId,
        setSubmissionFocusId,
        focusedSubmission,
        submissions,
        loadingSubmissions: loading,
        docSubmissionMap: docSubMap,
      }}
    >
      {children}
    </SubmissionFocusContext.Provider>
  );
}
